import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, LinearProgress,
  Alert, Chip, Divider, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Tabs, Tab, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  People, TrendingUp, TrendingDown, Psychology, TrackChanges,
  School, EmojiEvents, ReceiptLong, BeachAccess, Warning,
} from '@mui/icons-material';

function apiFetch(path: string) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(r => r.json().catch(() => ({})));
}

// ── Mini helpers ──────────────────────────────────────────────────────────────

function fmtNum(n: any, decimals = 0) {
  return Number(n ?? 0).toLocaleString('en-HK', { maximumFractionDigits: decimals });
}

function fmtHKD(n: any) {
  return `HK$${fmtNum(n)}`;
}

function progressColor(p: number) {
  if (p >= 70) return '#2e7d32';
  if (p >= 40) return '#f57c00';
  return '#c62828';
}

function moodColor(score: number): string {
  if (score >= 4) return '#2e7d32';
  if (score >= 3) return '#f57c00';
  return '#c62828';
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color, icon, alert }: any) {
  return (
    <Card sx={{ height: '100%', borderLeft: `4px solid ${color}` }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>{label}</Typography>
            <Typography variant="h4" fontWeight={800} color={color} sx={{ lineHeight: 1.2, mt: 0.3 }}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{ color, opacity: 0.7, mt: 0.5 }}>{icon}</Box>
        </Box>
        {alert && <Chip label={alert} size="small" color="error" sx={{ mt: 1, fontSize: '0.65rem' }} />}
      </CardContent>
    </Card>
  );
}

// ── Spark Bar ─────────────────────────────────────────────────────────────────

function SparkBar({ data, valueKey, labelKey, color = '#1565c0', maxValue }: any) {
  const values = data.map((d: any) => Number(d[valueKey] ?? 0));
  const max = maxValue ?? Math.max(...values, 1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 64 }}>
      {data.map((d: any, i: number) => {
        const v = Number(d[valueKey] ?? 0);
        const pct = (v / max) * 100;
        return (
          <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{v}</Typography>
            <Box sx={{ width: '100%', bgcolor: color, height: `${Math.max(pct, 4)}%`, minHeight: 3, borderRadius: '2px 2px 0 0', opacity: 0.85 }} />
            <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary', writingMode: 'vertical-rl', mt: 0.3 }}>
              {d[labelKey]}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Horizontal Bar Row ────────────────────────────────────────────────────────

function HBar({ label, value, max, color, suffix = '' }: any) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <Box sx={{ mb: 0.8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
        <Typography variant="caption" noWrap sx={{ maxWidth: '55%' }}>{label}</Typography>
        <Typography variant="caption" fontWeight={700} color={color}>{fmtNum(value)}{suffix}</Typography>
      </Box>
      <Box sx={{ height: 6, bgcolor: '#e0e0e0', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
      </Box>
    </Box>
  );
}

// ── Section Paper ─────────────────────────────────────────────────────────────

function Section({ title, icon, color, children }: any) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      </Box>
      {children}
    </Paper>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function WorkforceAnalyticsPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  useEffect(() => {
    apiFetch('/api/workforce-analytics/dashboard')
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <Box sx={{ p: 4 }}><LinearProgress /><Typography sx={{ mt: 2, color: 'text.secondary', textAlign: 'center' }}>Loading workforce analytics…</Typography></Box>;
  if (error || !data) return <Alert severity="error">Failed to load analytics: {error}</Alert>;

  const { attrition, wellness, wellnessTrend, goals, goalsByDept, training, kudos, kudusTrend, expenses, expensesByCat, leave, leaveByType, headcountTrend, deptBreakdown } = data;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <TrendingUp sx={{ fontSize: 32, color: '#1a237e' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Workforce Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Strategic people insights across all modules — live data</Typography>
        </Box>
      </Box>

      {/* Top KPI Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCard label="Active Headcount" value={fmtNum(attrition.activeHeadcount)} color="#1565c0" icon={<People />}
            sub={`+${attrition.hires3m} in 3m`} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCard label="Attrition Rate" value={`${attrition.attritionRate}%`}
            color={attrition.attritionRate > 20 ? '#c62828' : attrition.attritionRate > 10 ? '#f57c00' : '#2e7d32'}
            icon={attrition.attritionRate > 15 ? <TrendingDown /> : <TrendingUp />}
            sub="trailing 12 months"
            alert={attrition.attritionRate > 20 ? 'High Attrition' : undefined} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCard label="Avg Mood" value={Number(wellness.avg_mood ?? 0).toFixed(1)}
            color={moodColor(wellness.avg_mood)} icon={<Psychology />}
            sub={`${fmtNum(wellness.at_risk)} at risk`}
            alert={Number(wellness.at_risk) > 0 ? `${wellness.at_risk} burnout risk` : undefined} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCard label="OKR Avg Progress" value={`${fmtNum(goals.avg_progress)}%`}
            color={progressColor(Number(goals.avg_progress))} icon={<TrackChanges />}
            sub={`${fmtNum(goals.total)} total goals`} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCard label="Kudos Given (30d)" value={fmtNum(kudos.last30d)} color="#6a1b9a" icon={<EmojiEvents />}
            sub={`${fmtNum(kudos.unique_givers)} unique givers`} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCard label="Training Completion" value={`${fmtNum(training.avg_completion)}%`}
            color={progressColor(Number(training.avg_completion))} icon={<School />}
            sub={`${fmtNum(training.unique_learners)} learners`} />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="People & Headcount" />
          <Tab label="Wellness & Culture" />
          <Tab label="Goals & Learning" />
          <Tab label="Finance" />
        </Tabs>
      </Paper>

      {/* ── TAB 0: People ── */}
      {tab === 0 && (
        <Grid container spacing={2}>
          {/* Headcount Trend */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Section title="Headcount Trend (6 months)" icon={<People />} color="#1565c0">
              {headcountTrend?.length ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 100 }}>
                    {headcountTrend.map((m: any, i: number) => {
                      const max = Math.max(...headcountTrend.map((x: any) => Number(x.headcount)), 1);
                      const hc = Number(m.headcount);
                      return (
                        <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>{hc}</Typography>
                          <Box sx={{ width: '80%', bgcolor: '#1565c0', height: `${(hc / max) * 80}px`, minHeight: 4, borderRadius: '2px 2px 0 0' }} />
                          {Number(m.new_hires) > 0 && <Box sx={{ width: '80%', bgcolor: '#81d4fa', height: `${(Number(m.new_hires) / max) * 80}px`, mt: 0.3, borderRadius: '2px 2px 0 0', opacity: 0.7 }} />}
                          <Typography variant="caption" sx={{ fontSize: '0.58rem', color: 'text.secondary', mt: 0.5 }}>{m.label}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Box sx={{ width: 10, height: 10, bgcolor: '#1565c0', borderRadius: 1 }} />
                      <Typography variant="caption">Headcount</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Box sx={{ width: 10, height: 10, bgcolor: '#81d4fa', borderRadius: 1 }} />
                      <Typography variant="caption">New Hires</Typography>
                    </Box>
                  </Box>
                </Box>
              ) : <Typography variant="body2" color="text.secondary">No headcount data yet.</Typography>}
            </Section>
          </Grid>

          {/* Attrition Summary */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Section title="Attrition Summary" icon={<TrendingDown />} color={attrition.attritionRate > 15 ? '#c62828' : '#2e7d32'}>
              {[
                { label: 'Net Growth (12m)', value: `${attrition.netGrowth12m > 0 ? '+' : ''}${attrition.netGrowth12m}`, color: attrition.netGrowth12m >= 0 ? '#2e7d32' : '#c62828' },
                { label: 'Hires — 12m', value: attrition.hires12m, color: '#1565c0' },
                { label: 'Hires — 3m', value: attrition.hires3m, color: '#1565c0' },
                { label: 'Leavers — 12m', value: attrition.leavers12m, color: '#f57c00' },
                { label: 'Leavers — 3m', value: attrition.leavers3m, color: '#f57c00' },
              ].map(item => (
                <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                  <Typography fontWeight={700} color={item.color}>{item.value}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Annualised Attrition</Typography>
                <Typography variant="h6" fontWeight={800}
                  color={attrition.attritionRate > 20 ? '#c62828' : attrition.attritionRate > 10 ? '#f57c00' : '#2e7d32'}>
                  {attrition.attritionRate}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Industry benchmark: 13–18% for tech</Typography>
            </Section>
          </Grid>

          {/* Dept Breakdown */}
          <Grid size={{ xs: 12 }}>
            <Section title="Headcount by Department" icon={<People />} color="#1565c0">
              {deptBreakdown?.length ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><b>Department</b></TableCell>
                        <TableCell align="right"><b>Headcount</b></TableCell>
                        <TableCell align="right"><b>Active</b></TableCell>
                        <TableCell align="right"><b>Avg Tenure (mo)</b></TableCell>
                        <TableCell sx={{ width: '30%' }}><b>Size</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deptBreakdown.map((d: any) => {
                        const maxHc = Math.max(...deptBreakdown.map((x: any) => Number(x.headcount)));
                        return (
                          <TableRow key={d.department} hover>
                            <TableCell><Typography fontWeight={600}>{d.department ?? 'Unassigned'}</Typography></TableCell>
                            <TableCell align="right">{d.headcount}</TableCell>
                            <TableCell align="right">{d.active}</TableCell>
                            <TableCell align="right">{d.avg_tenure_months ?? '—'}</TableCell>
                            <TableCell>
                              <Box sx={{ height: 8, bgcolor: '#e3f2fd', borderRadius: 4, overflow: 'hidden' }}>
                                <Box sx={{ width: `${(Number(d.headcount) / maxHc) * 100}%`, height: '100%', bgcolor: '#1565c0', borderRadius: 4 }} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : <Typography color="text.secondary">No department data.</Typography>}
            </Section>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 1: Wellness & Culture ── */}
      {tab === 1 && (
        <Grid container spacing={2}>
          {/* Wellness KPIs */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Section title="4-Week Wellness Snapshot" icon={<Psychology />} color="#00695c">
              {[
                { label: 'Avg Mood', value: Number(wellness.avg_mood ?? 0).toFixed(1), max: 5, color: moodColor(wellness.avg_mood) },
                { label: 'Avg Energy', value: Number(wellness.avg_energy ?? 0).toFixed(1), max: 5, color: '#1565c0' },
                { label: 'Avg Stress', value: Number(wellness.avg_stress ?? 0).toFixed(1), max: 5, color: '#f57c00' },
              ].map(item => (
                <Box key={item.label} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                    <Typography variant="body2">{item.label}</Typography>
                    <Typography fontWeight={700} color={item.color}>{item.value} / 5</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={(Number(item.value) / 5) * 100}
                    sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: item.color } }} />
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={800} color="#00695c">{fmtNum(wellness.participants)}</Typography>
                  <Typography variant="caption" color="text.secondary">Participants</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={800} color={Number(wellness.at_risk) > 0 ? '#c62828' : '#2e7d32'}>{wellness.at_risk}</Typography>
                  <Typography variant="caption" color="text.secondary">Burnout Risk</Typography>
                </Box>
              </Box>
            </Section>
          </Grid>

          {/* Wellness Trend */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Section title="Mood Trend (8 weeks)" icon={<Psychology />} color="#00695c">
              {wellnessTrend?.length ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell>Week</TableCell>
                          <TableCell align="right">Mood</TableCell>
                          <TableCell align="right">Energy</TableCell>
                          <TableCell align="right">Stress</TableCell>
                          <TableCell align="right">Responses</TableCell>
                          <TableCell align="right">Burnout</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {wellnessTrend.map((w: any) => (
                          <TableRow key={w.week} hover>
                            <TableCell>{w.week}</TableCell>
                            <TableCell align="right"><Typography color={moodColor(w.avg_mood)} fontWeight={700}>{Number(w.avg_mood).toFixed(1)}</Typography></TableCell>
                            <TableCell align="right">{Number(w.avg_energy).toFixed(1)}</TableCell>
                            <TableCell align="right" sx={{ color: Number(w.avg_stress) > 3.5 ? '#c62828' : 'inherit' }}>{Number(w.avg_stress).toFixed(1)}</TableCell>
                            <TableCell align="right">{w.responses}</TableCell>
                            <TableCell align="right">
                              {Number(w.burnout_count) > 0 ? <Chip label={w.burnout_count} size="small" color="error" /> : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : <Typography color="text.secondary" variant="body2">No wellness data yet.</Typography>}
            </Section>
          </Grid>

          {/* Kudos */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Section title="Recognition & Kudos" icon={<EmojiEvents />} color="#6a1b9a">
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                  { label: 'Total Kudos (12m)', value: fmtNum(kudos.total) },
                  { label: 'Last 30 days', value: fmtNum(kudos.last30d) },
                  { label: 'Total Points Awarded', value: fmtNum(kudos.total_points) },
                  { label: 'Unique Receivers', value: fmtNum(kudos.unique_receivers) },
                ].map(k => (
                  <Grid key={k.label} size={{ xs: 6 }}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                      <Typography variant="h5" fontWeight={800} color="#6a1b9a">{k.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {kudusTrend?.length > 0 && (
                <SparkBar data={kudusTrend} valueKey="kudos_given" labelKey="month" color="#6a1b9a" />
              )}
            </Section>
          </Grid>

          {/* Leave */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Section title="Leave Utilisation (12m)" icon={<BeachAccess />} color="#e65100">
              {[
                { label: 'Total Requests', value: fmtNum(leave.total_requests), color: '#1565c0' },
                { label: 'Days Taken', value: fmtNum(leave.total_days_taken), color: '#e65100' },
                { label: 'Avg Days / Request', value: fmtNum(leave.avg_days, 1), color: '#2e7d32' },
                { label: 'Pending Approval', value: fmtNum(leave.pending), color: leave.pending > 10 ? '#c62828' : '#f57c00' },
              ].map(k => (
                <Box key={k.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">{k.label}</Typography>
                  <Typography fontWeight={700} color={k.color}>{k.value}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              {leaveByType?.slice(0, 4).map((lt: any) => (
                <HBar key={lt.leave_type} label={lt.leave_type} value={Number(lt.total_days)}
                  max={Math.max(...(leaveByType.map((x: any) => Number(x.total_days))), 1)}
                  color="#e65100" suffix=" days" />
              ))}
            </Section>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 2: Goals & Learning ── */}
      {tab === 2 && (
        <Grid container spacing={2}>
          {/* OKR Summary */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Section title="OKR Status" icon={<TrackChanges />} color="#1a237e">
              <Box sx={{ mb: 2 }}>
                <Typography variant="h3" fontWeight={800} color={progressColor(Number(goals.avg_progress))} sx={{ lineHeight: 1 }}>
                  {fmtNum(goals.avg_progress)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">Average goal progress ({fmtNum(goals.total)} total)</Typography>
                <LinearProgress variant="determinate" value={Number(goals.avg_progress)}
                  sx={{ mt: 1, height: 10, borderRadius: 5, '& .MuiLinearProgress-bar': { bgcolor: progressColor(Number(goals.avg_progress)) } }} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[
                { label: '✅ On Track', value: goals.on_track, color: '#2e7d32' },
                { label: '⚠️ At Risk', value: goals.at_risk, color: '#f57c00' },
                { label: '🔴 Off Track', value: goals.off_track, color: '#c62828' },
                { label: '🏁 Completed', value: goals.completed, color: '#1565c0' },
              ].map(g => (
                <Box key={g.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                  <Typography variant="body2">{g.label}</Typography>
                  <Typography fontWeight={700} color={g.color}>{g.value}</Typography>
                </Box>
              ))}
            </Section>
          </Grid>

          {/* Goals by dept */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Section title="Goal Progress by Department" icon={<TrackChanges />} color="#1a237e">
              {goalsByDept?.length ? goalsByDept.map((d: any) => (
                <Box key={d.department} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                    <Typography variant="body2" fontWeight={600}>{d.department}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">{d.total} goals</Typography>
                      <Typography variant="caption" fontWeight={700} color={progressColor(Number(d.avg_progress))}>
                        {Number(d.avg_progress).toFixed(0)}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress variant="determinate" value={Number(d.avg_progress)}
                    sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: progressColor(Number(d.avg_progress)) } }} />
                </Box>
              )) : <Typography color="text.secondary" variant="body2">No goals data yet.</Typography>}
            </Section>
          </Grid>

          {/* Training */}
          <Grid size={{ xs: 12 }}>
            <Section title="Training & Learning" icon={<School />} color="#2e7d32">
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  {[
                    { label: 'Total Enrollments', value: fmtNum(training.total_enrollments), color: '#1565c0' },
                    { label: 'Completed', value: fmtNum(training.completed), color: '#2e7d32' },
                    { label: 'In Progress', value: fmtNum(training.in_progress), color: '#f57c00' },
                    { label: 'Unique Learners', value: fmtNum(training.unique_learners), color: '#6a1b9a' },
                    { label: 'Avg Completion', value: `${fmtNum(training.avg_completion)}%`, color: progressColor(Number(training.avg_completion)) },
                  ].map(t => (
                    <Box key={t.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                      <Typography variant="body2" color="text.secondary">{t.label}</Typography>
                      <Typography fontWeight={700} color={t.color}>{t.value}</Typography>
                    </Box>
                  ))}
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>BY CATEGORY</Typography>
                  {(data.training?.categories ?? []).map((c: any) => (
                    <HBar key={c.category} label={c.category}
                      value={Number(c.enrollments)}
                      max={Math.max(...(data.training?.categories?.map((x: any) => Number(x.enrollments)) ?? [1]), 1)}
                      color="#2e7d32" />
                  ))}
                </Grid>
              </Grid>
            </Section>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 3: Finance ── */}
      {tab === 3 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Section title="Expense Claims (12m)" icon={<ReceiptLong />} color="#b71c1c">
              {[
                { label: 'Total Claims', value: fmtNum(expenses.total_claims), color: '#1565c0' },
                { label: 'Approved', value: fmtNum(expenses.approved), color: '#2e7d32' },
                { label: 'Pending', value: fmtNum(expenses.pending), color: expenses.pending > 20 ? '#c62828' : '#f57c00' },
                { label: 'Total Approved', value: fmtHKD(expenses.total_approved_hkd), color: '#b71c1c' },
                { label: 'Avg Claim', value: fmtHKD(expenses.avg_claim), color: '#6a1b9a' },
              ].map(e => (
                <Box key={e.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">{e.label}</Typography>
                  <Typography fontWeight={700} color={e.color}>{e.value}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>BY CATEGORY</Typography>
              {expensesByCat?.slice(0, 6).map((c: any) => (
                <HBar key={c.category} label={c.category}
                  value={Number(c.total)}
                  max={Math.max(...(expensesByCat.map((x: any) => Number(x.total))), 1)}
                  color="#b71c1c" />
              ))}
            </Section>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Section title="Financial Highlights" icon={<TrendingUp />} color="#2e7d32">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Key financial metrics across payroll, expenses, and budget utilization.
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'Claims Approved', value: fmtHKD(expenses.total_approved_hkd), icon: '💰', color: '#b71c1c' },
                  { label: 'Leave Days (12m)', value: fmtNum(leave.total_days_taken), icon: '🏖', color: '#e65100' },
                  { label: 'Kudos Points', value: fmtNum(kudos.total_points), icon: '⭐', color: '#6a1b9a' },
                  { label: 'Training Learners', value: fmtNum(training.unique_learners), icon: '📚', color: '#2e7d32' },
                ].map(item => (
                  <Grid key={item.label} size={{ xs: 6 }}>
                    <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                      <Typography sx={{ fontSize: 28, lineHeight: 1.2 }}>{item.icon}</Typography>
                      <Typography variant="h5" fontWeight={800} color={item.color}>{item.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Section>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
